import React, { useState, useEffect, useRef } from "react";
import axios from "../axios";

const POSTS_PER_PAGE = 10;

export default function BbsThread({ thread, onBack, currentUser }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [postError, setPostError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingId, setDeletingId] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchPosts();
  }, [thread.id]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/threads/${thread.id}`);
      const sorted = [...(res.data.posts || [])].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      setPosts(sorted);
      setCurrentPage(Math.ceil(sorted.length / POSTS_PER_PAGE) || 1);
    } catch {
      setError("投稿の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setPostError(null);
    try {
      setSubmitting(true);
      const res = await axios.post(`/threads/${thread.id}/posts`, { content });
      const newPost = res.data;
      const updated = [...posts, newPost];
      setPosts(updated);
      setContent("");
      setCurrentPage(Math.ceil(updated.length / POSTS_PER_PAGE));
    } catch (err) {
      setPostError(
        err?.response?.data?.message || "投稿に失敗しました。再度お試しください。"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("この投稿を削除しますか？")) return;
    try {
      setDeletingId(postId);
      await axios.delete(`/threads/${thread.id}/posts/${postId}`);
      const updated = posts.filter((p) => p.id !== postId);
      setPosts(updated);
      // 削除後にページが空になったら前のページへ
      const newTotal = Math.ceil(updated.length / POSTS_PER_PAGE) || 1;
      if (currentPage > newTotal) setCurrentPage(newTotal);
    } catch (err) {
      alert(err?.response?.data?.message || "削除に失敗しました。");
    } finally {
      setDeletingId(null);
    }
  };

  // スレッドごと消せる・他人の投稿を消せるのは super_admin だけ
  const isAdmin = currentUser?.user_role_id === 1;
  const [deletingThread, setDeletingThread] = useState(false);

  const handleDeleteThread = async () => {
    if (!window.confirm(`スレッド「${thread.title}」を削除しますか？\n${posts.length} 件の投稿もすべて削除されます。`)) return;
    try {
      setDeletingThread(true);
      await axios.delete(`/threads/${thread.id}`);
      onBack();
    } catch (err) {
      alert(err?.response?.data?.message || "スレッドの削除に失敗しました。");
      setDeletingThread(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString("ja-JP", {
      timeZone: "Asia/Tokyo",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
  const paginatedPosts = posts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const handleTextareaInput = (e) => {
    setContent(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = `${Math.min(e.target.scrollHeight, 240)}px`;
  };

  return (
    <div className="bbs-thread-wrapper">
      {/* スレッドヘッダー */}
      <div className="thread-header">
        <button className="btn-back" onClick={onBack}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          スレッド一覧
        </button>
        <div className="thread-header-title">
          {thread.category && (
            <span className="thread-cat-badge">{thread.category}</span>
          )}
          <h2 className="thread-name">{thread.title}</h2>
        </div>
        <div className="thread-header-footer">
          <span className="thread-post-total">{posts.length} 件の投稿</span>
          {isAdmin && (
            <button
              className="btn-delete-thread"
              onClick={handleDeleteThread}
              disabled={deletingThread}
            >
              {deletingThread ? "削除中..." : "スレッドを削除"}
            </button>
          )}
        </div>
      </div>

      {/* 返信フォーム（最上部） */}
      <form className="reply-form" onSubmit={handleSubmit}>
        <div className="reply-form-inner">
          <div className="reply-form-label">返信を書く</div>
          <textarea
            ref={textareaRef}
            className="reply-textarea"
            placeholder="内容を入力..."
            value={content}
            onChange={handleTextareaInput}
            maxLength={2000}
            rows={3}
            disabled={submitting}
          />
          <div className="reply-form-footer">
            <span className="char-counter">{content.length} / 2000</span>
            {postError && <span className="post-error">{postError}</span>}
            <button
              className="btn-post"
              type="submit"
              disabled={submitting || !content.trim()}
            >
              {submitting ? "送信中..." : "投稿する"}
            </button>
          </div>
        </div>
      </form>

      {/* 投稿一覧 */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <span>読み込み中...</span>
        </div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : posts.length === 0 ? (
        <div className="empty-state">まだ投稿がありません。最初の一言をどうぞ。</div>
      ) : (
        <>
          {totalPages > 1 && (
            <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
          )}

          <ol className="post-list" start={(currentPage - 1) * POSTS_PER_PAGE + 1}>
            {paginatedPosts.map((post, idx) => {
              const globalIdx = (currentPage - 1) * POSTS_PER_PAGE + idx + 1;
              const isOwner = currentUser && post.user_id === currentUser.id;
              const roleName = post.user?.role_name ?? "user";
              const isStaff = roleName === "super_admin" || roleName === "operator";
              return (
                <li key={post.id} className={`post-item${isStaff ? " post-item--staff" : ""}`}>
                  <div className="post-header">
                    <div className={`post-avatar${isStaff ? " post-avatar--staff" : ""}`}>
                      {(post.user?.name ?? "？")[0]}
                    </div>
                    {/* 1 段目に名前（スマホでも削らない）、2 段目に番号・バッジ・日時。
                        頭文字と削除ボタンは 2 段の縦の真ん中 */}
                    <div className="post-header-body">
                      <span className="post-username">
                        {post.user?.name ?? "匿名"}
                      </span>
                      <div className="post-header-sub">
                        <span className="post-num">#{globalIdx}</span>
                        <span className={`role-badge role-badge--${isStaff ? "staff" : "user"}`}>
                          {isStaff ? "運営者投稿" : "一般者投稿"}
                        </span>
                        <span className="post-date">{formatDate(post.created_at)}</span>
                      </div>
                    </div>
                    {(isOwner || isAdmin) && (
                      <button
                        className="btn-delete-post"
                        onClick={() => handleDelete(post.id)}
                        disabled={deletingId === post.id}
                        title="削除"
                      >
                        {deletingId === post.id ? "..." : (
                          <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                            <path d="M2 4h12M6 4V2h4v2M5 4l1 9h4l1-9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                  <div className="post-content">{post.content}</div>
                </li>
              );
            })}
          </ol>

          {totalPages > 1 && (
            <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
          )}
        </>
      )}
    </div>
  );
}

function Pagination({ current, total, onChange }) {
  const getVisiblePages = () => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const left = Math.max(1, current - 2);
    const right = Math.min(total, current + 2);
    const result = [];
    if (left > 1) { result.push(1); if (left > 2) result.push("..."); }
    for (let i = left; i <= right; i++) result.push(i);
    if (right < total) { if (right < total - 1) result.push("..."); result.push(total); }
    return result;
  };

  return (
    <div className="pagination">
      <button className="page-btn" onClick={() => onChange(current - 1)} disabled={current === 1}>‹</button>
      {getVisiblePages().map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} className="page-ellipsis">…</span>
        ) : (
          <button
            key={p}
            className={`page-btn ${p === current ? "active" : ""}`}
            onClick={() => onChange(p)}
          >
            {p}
          </button>
        )
      )}
      <button className="page-btn" onClick={() => onChange(current + 1)} disabled={current === total}>›</button>
    </div>
  );
}
